import React from "react";
import { Sun, Moon } from "lucide-react";
import { useTheme } from "@/hooks/useTheme";

/**
 * ThemeToggle — sun/moon segmented pill.
 *
 * Visual: a two-cell rounded-full container, the active mode highlighted with
 * a surface pill. Mirrors the segmented-control pattern used elsewhere
 * (admin queue tabs) so it reads as part of the UI vocabulary, not bolted on.
 */
export function ThemeToggle({ className = "" }) {
  const { theme, setTheme } = useTheme();
  const isDark = theme === "dark";

  return (
    <div
      data-testid="theme-toggle"
      role="group"
      aria-label="Theme"
      className={`relative inline-flex items-center bg-surface-alt border border-line rounded-full p-0.5 shadow-xs ${className}`}
    >
      <button
        type="button"
        aria-label="Switch to light mode"
        aria-pressed={!isDark}
        data-testid="theme-toggle-light"
        onClick={() => setTheme("light")}
        className={`size-8 grid place-items-center rounded-full transition-all ${
          !isDark ? "bg-surface text-ink shadow-sm" : "text-ink-hint hover:text-ink-muted"
        }`}
      >
        <Sun className="size-4" strokeWidth={2.2} />
      </button>
      <button
        type="button"
        aria-label="Switch to dark mode"
        aria-pressed={isDark}
        data-testid="theme-toggle-dark"
        onClick={() => setTheme("dark")}
        className={`size-8 grid place-items-center rounded-full transition-all ${
          isDark ? "bg-surface text-brand-700 shadow-sm" : "text-ink-hint hover:text-ink-muted"
        }`}
      >
        <Moon className="size-4" strokeWidth={2.2} />
      </button>
    </div>
  );
}

export default ThemeToggle;
